import { useRef, useState, type KeyboardEvent, type PointerEvent, type MouseEvent, type SyntheticEvent } from 'react';
import { dismissRule } from './dismissRule.ts';

/**
 * No pop-up closes on a stray click (A-#195 2, Arthur, 27 October 2026): he selected text in a note, let go outside
 * the card, and it closed with his edit. Every modal dialog takes its ways out through this:
 *
 * - a backdrop click closes only when the pointer went DOWN and UP on the backdrop itself (a click on a native
 *   <dialog>'s backdrop lands on the dialog element); a drag that starts in a field and ends outside is not one;
 * - while a field typed into is still on the page (dismissRule.ts: a refused save keeps it, a saved form leaves), a backdrop click,
 *   Escape or Close does not close it silently: it asks "Discard your changes?" in the dialog itself, never a
 *   browser prompt. `changes: false` (a search box) skips the asking and keeps the backdrop rule.
 *
 * The rule is `dismissRule` (dismissRule.ts), plain so it is tested without a browser; the hook only holds it.
 */
export function useDismissGuard(dismiss: () => void, options: { changes?: boolean } = {}) {
  const [asking, setAsking] = useState(false);
  const closeRef = useRef(dismiss);
  closeRef.current = dismiss;
  // the pop-up itself, from its own events: where a remounted field of the same name is looked for (A-#201)
  const root = useRef<Element | null>(null);
  const present = (name: string) => [...(root.current?.querySelectorAll('input, textarea, select') ?? [])]
    .some((f) => f.isConnected && fieldName(f) === name);
  const rule = useRef<ReturnType<typeof dismissRule> | null>(null);
  rule.current ??= dismissRule(() => closeRef.current(), () => setAsking(true), { ...options, present });
  const r = rule.current;
  const changed = (e: SyntheticEvent<Element>) => {
    root.current = e.currentTarget;
    const f = e.target as Element;
    r.changed({ get isConnected() { return f.isConnected; }, name: fieldName(f) });
  };
  return {
    asking,
    /** Close, Escape and the backdrop all come here. */
    request: r.request,
    discard: () => { setAsking(false); r.discarded(); closeRef.current(); },
    keep: () => setAsking(false),
    /** For a pop-up that is not a <dialog> (ContactDuplicates' merge bar): a field typed into. */
    changed: (e: SyntheticEvent<Element>) => changed(e),
    dialogProps: {
      onPointerDown: (e: PointerEvent<HTMLDialogElement>) => r.pointerDown(e.target === e.currentTarget),
      onClick: (e: MouseEvent<HTMLDialogElement>) => r.click(e.target === e.currentTarget),
      onKeyDown: (e: KeyboardEvent<HTMLDialogElement>) => { if (e.key === 'Escape') { e.preventDefault(); r.request(); } },
      // the browser's own Escape close, which would skip the asking; it also comes when focus has left the dialog
      // (Keep editing removes the button that held it), where onKeyDown never sees the key: so it asks here too
      onCancel: (e: SyntheticEvent<HTMLDialogElement>) => { e.preventDefault(); r.request(); },
      // the field typed into; a submit clears nothing (A-#199 1: a refused save keeps its text, and asking)
      onInput: changed,
      onChange: changed,
    },
  };
}

/**
 * A field's name, which survives its element being drawn again: its id, its name, its label's words, or its
 * aria-label. Null for a field with none of them, which is then known by its element alone.
 */
function fieldName(f: Element): string | null {
  const el = f as HTMLInputElement;
  return el.id || el.getAttribute('name') || el.labels?.[0]?.textContent?.trim() || el.getAttribute('aria-label') || null;
}

/** Sheet.tsx's secondary button (not imported: Sheet imports this). */
const button = 'min-h-[44px] rounded-lg border border-neutral-300 bg-surface px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

/** The question, inside the dialog, at its top: Discard, or Keep editing (where focus goes). */
export function DiscardAsk({ guard }: { guard: { asking: boolean; discard: () => void; keep: () => void } }) {
  if (!guard.asking) return null;
  return (
    <div role="alertdialog" aria-label="Discard your changes?"
         className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-warning-edge bg-warning-bg px-4 py-2 text-warning-fg">
      <p className="mr-auto font-semibold">Discard your changes?</p>
      <button type="button" className={button} onClick={guard.discard}>Discard</button>
      <button type="button" className={button} autoFocus onClick={guard.keep}>Keep editing</button>
    </div>
  );
}
